import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import closePopupOperation from 'editor/src/store/app/module/popup/operation/closePopupOperation';
import getPopupData from 'editor/src/store/app/module/popup/selector/getPopupData';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import getSwatchBackground from 'editor/src/util/color/getSwatchBackground';
import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import Button from 'editor/src/component/Button';
import IconCross from 'editor/src/component/Icon/IconCross';
import { PopupName } from 'editor/src/component/Popup/popups';

import styles from './index.module.scss';

export interface BatchSelectionRow {
  /** Opaque row id, echoed back to the host with the selected quantity. */
  id: string;
  label: string;
  available: boolean;
  minQuantity: number;
  initialQuantity: number;
  maxQuantity: number;
  /** Optional group (e.g. a colour) the row belongs to. */
  groupId?: string;
}

export interface BatchSelectionGroup {
  id: string;
  label: string;
  /** Optional swatch colour rendered on the group's chip; `;`-separated for multicolour. */
  colorHex?: string;
  /** Optional swatch texture (e.g. heather, triblend) layered over the colour. */
  texture?: string;
}

/** Footer strings arrive pre-formatted — pricing stays host-side. */
export interface BatchSelectionTotals {
  totalText: string;
  priceText: string;
  priceInitialText: string;
  canConfirm: boolean;
}

export interface BatchSelectionItem {
  id: string;
  quantity: number;
}

export interface BatchSelectionPopupData {
  /** Hosts may override the editor's own translated labels. */
  title?: string;
  confirmText?: string;
  unavailableText?: string;
  rows: BatchSelectionRow[];
  /** When present, a swatch bar switches which group's rows are shown. */
  groups?: BatchSelectionGroup[];
  totals: BatchSelectionTotals;
}

// Every element the drawer may hold, so Tab keeps cycling if the layout grows
// beyond buttons (e.g. a numeric quantity input).
const FOCUSABLE_SELECTOR = [
  'button:not(:disabled)',
  '[href]',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function clamp(row: BatchSelectionRow, quantity: number): number {
  return Math.max(row.minQuantity, Math.min(quantity, row.maxQuantity));
}

// Neutral grey when the host sends neither a usable colour nor a texture.
function getGroupSwatchStyle(group: BatchSelectionGroup): React.CSSProperties {
  const style = getSwatchBackground(group.colorHex, group.texture);
  return style.backgroundColor || style.backgroundImage ? style : { backgroundColor: '#e0e0e0' };
}

function buildItems(rows: BatchSelectionRow[], quantities: { [id: string]: number }): BatchSelectionItem[] {
  return rows
    .filter((row) => row.available && (quantities[row.id] || 0) > 0)
    .map((row) => ({ id: row.id, quantity: quantities[row.id] }));
}

/**
 * Host-driven multipack drawer (right-side sheet on desktop, bottom sheet on
 * mobile). Groups (colours) render as a swatch bar with per-group selected
 * counts; the size rows below always show one group at a time. The host
 * supplies rows, labels and pre-formatted prices via batchSelection.setData and
 * owns the commit; see docs/batch-selection.md.
 */
function BatchSelectionPopup() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const popupData = useSelector((state) => getPopupData(state, PopupName.BATCH_SELECTION_POPUP));
  const rows = popupData?.rows;
  const groups = popupData?.groups;
  const drawerRef = useRef<HTMLElement>(null);
  const [quantities, setQuantities] = useState<{ [id: string]: number }>({});
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(undefined);

  // Re-seed quantities and the visible group only when the host pushes a new
  // row set (totals updates merge into the popup data but keep the rows
  // reference). The initially visible group is the one holding pre-seeded
  // quantities — the variant the design is bound to.
  useEffect(() => {
    if (!rows) {
      return;
    }
    const seed: { [id: string]: number } = {};
    rows.forEach((row) => {
      // Clamp: a host sending an initial quantity outside its own bounds would
      // otherwise start the row out-of-range until the customer steps it.
      seed[row.id] = clamp(row, row.initialQuantity);
    });
    setQuantities(seed);
    const seededRow = rows.find((row) => row.initialQuantity > 0 && row.groupId);
    setSelectedGroupId(seededRow?.groupId || groups?.[0]?.id);
  }, [rows, groups]);

  // Escape closes; Tab cycles within the drawer; initial focus lands on it.
  const isOpen = !!popupData;
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    drawerRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        sendPostMessage('batchSelection.dismissed', undefined);
        dispatch(closePopupOperation());
        return;
      }
      if (e.key !== 'Tab' || !drawerRef.current) {
        return;
      }
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!e.shiftKey && (active === last || !drawerRef.current.contains(active))) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || !drawerRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const countByGroup = useMemo(() => {
    const counts: { [groupId: string]: number } = {};
    (rows || []).forEach((row) => {
      if (row.groupId && row.available) {
        counts[row.groupId] = (counts[row.groupId] || 0) + (quantities[row.id] || 0);
      }
    });
    return counts;
  }, [rows, quantities]);

  if (!popupData) {
    return null;
  }

  const setQuantity = (row: BatchSelectionRow, next: number) => {
    const updated = { ...quantities, [row.id]: clamp(row, next) };
    setQuantities(updated);
    sendPostMessage('batchSelection.selectionChanged', { items: buildItems(popupData.rows, updated) });
  };

  const dismiss = () => {
    sendPostMessage('batchSelection.dismissed', undefined);
    dispatch(closePopupOperation());
  };

  const confirm = () => {
    const items = buildItems(popupData.rows, quantities);
    if (items.length === 0) {
      return;
    }
    sendPostMessage('batchSelection.confirmed', { items });
    dispatch(closePopupOperation());
  };

  const { totals } = popupData;
  const title = popupData.title || t('txt_editor_batch_selection_title');
  const confirmText = popupData.confirmText || t('txt_editor_batch_selection_confirm');
  const unavailableText = popupData.unavailableText || t('txt_editor_batch_selection_unavailable');
  const hasGroups = !!groups && groups.length > 0;
  const selectedGroup = hasGroups ? groups.find((group) => group.id === selectedGroupId) || groups[0] : undefined;
  const visibleRows = hasGroups ? popupData.rows.filter((row) => row.groupId === selectedGroup?.id) : popupData.rows;

  const renderRow = (row: BatchSelectionRow) => {
    const quantity = quantities[row.id] || 0;
    return (
      <div className={cn(styles.row, 'cy-batch-row', { [styles.active]: quantity > 0 })} key={row.id}>
        <span className={styles.label}>{row.label}</span>
        {row.available ? (
          <div className={styles.stepper}>
            <Button
              secondary
              noPadding
              disabled={quantity <= row.minQuantity}
              onClick={() => setQuantity(row, quantity - 1)}
              className={cn(styles.step, 'cy-batch-qty-dec')}
            >
              −
            </Button>
            <span className={cn(styles.quantity, 'cy-batch-qty')}>{quantity}</span>
            <Button
              secondary
              noPadding
              disabled={quantity >= row.maxQuantity}
              onClick={() => setQuantity(row, quantity + 1)}
              className={cn(styles.step, 'cy-batch-qty-inc')}
            >
              +
            </Button>
          </div>
        ) : (
          <span className={styles.unavailable}>{unavailableText}</span>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={cn(styles.backdrop, 'cy-popup-backdrop')} onClick={dismiss} />
      <aside
        ref={drawerRef}
        tabIndex={-1}
        className={cn(styles.drawer, 'cy-popup-batch-selection')}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          <button type="button" className={cn(styles.close, 'cy-popup-close')} onClick={dismiss} aria-label="Close">
            <IconCross />
          </button>
        </div>

        {hasGroups && (
          <div className={styles.swatchSection}>
            <div className={cn(styles.swatchBar, 'cy-batch-swatch-bar')}>
              {groups.map((group) => {
                const count = countByGroup[group.id] || 0;
                return (
                  <button
                    type="button"
                    key={group.id}
                    title={group.label}
                    onClick={() => setSelectedGroupId(group.id)}
                    className={cn(styles.swatchChip, 'cy-batch-swatch', {
                      [styles.selected]: group.id === selectedGroup?.id,
                    })}
                  >
                    <span className={cn(styles.swatch, 'cy-batch-swatch-color')} style={getGroupSwatchStyle(group)} />
                    {count > 0 && <span className={cn(styles.badge, 'cy-batch-swatch-count')}>{count}</span>}
                  </button>
                );
              })}
            </div>
            <div className={cn(styles.groupLabel, 'cy-batch-group-label')}>{selectedGroup?.label}</div>
          </div>
        )}

        <div className={styles.rows}>{visibleRows.map(renderRow)}</div>

        <div className={styles.footer}>
          <div className={styles.totals}>
            <span className={cn(styles.totalText, 'cy-batch-total-text')}>{totals.totalText}</span>
            <span className={styles.prices}>
              {!!totals.priceInitialText && (
                <span className={cn(styles.priceInitial, 'cy-batch-price-initial')}>{totals.priceInitialText}</span>
              )}
              <span className={cn(styles.price, 'cy-batch-price')}>{totals.priceText}</span>
            </span>
          </div>
          <Button
            primary
            fullWidth
            disabled={!totals.canConfirm}
            onClick={confirm}
            className={cn(styles.confirm, 'cy-popup-batch-confirm')}
          >
            {confirmText}
          </Button>
        </div>
      </aside>
    </>
  );
}

export default React.memo(BatchSelectionPopup);
